import { type QueryKey, useMutation, useQuery, type QueryClient, useQueryClient } from "@tanstack/react-query";

import { useRef, useSyncExternalStore } from "react";

import { useUsuario, type Usuario } from "@/features/acceso/api";
import { ApiError, apiGet, apiPost } from "@/lib/api";
import type { Esquema } from "@/lib/contrato";

import type { LineBoard } from "./lineas/modelo";
import type { ProductionBoard } from "./modelo";

export type AccionDeProduccion = Esquema<"BoardHistoryAction">;
type Estado = Esquema<"BoardHistoryStatus">;
type Resultado = Esquema<"BoardHistoryResult">;
export type LineaDelHistorial = "BA" | "CA" | "FU";
const RUTA = "/production/board/history";
const colas = new Map<string, Promise<unknown>>();
const sesiones = new Map<string, string>();
const intenciones = new Map<string, Esquema<"BoardHistoryCommand">>();
const compensaciones = new Map<string, Esquema<"BoardHistoryApply">>();
const pendientes = new Map<string, number>();
const oyentes = new Set<() => void>();
function pendiente(id: string, delta: number) {
  pendientes.set(id, Math.max(0, (pendientes.get(id) ?? 0) + delta));
  for (const avisar of oyentes) avisar();
}

/** Una pestaña y una persona; recargar conserva sus quince pasos, cambiar de usuario los separa. */
function sesion(qc: QueryClient) {
  const actor = qc.getQueryData<Usuario | null>(["usuario"])?.id ?? "local";
  const clave = `neca.production.history.${actor}`;
  let id = sesiones.get(clave);
  if (!id) {
    try { id = sessionStorage.getItem(clave) ?? undefined; } catch { /* Navegador sin almacenamiento. */ }
    id ??= crypto.randomUUID();
    sesiones.set(clave, id);
    try { sessionStorage.setItem(clave, id); } catch { /* La sesión en memoria sigue siendo válida. */ }
  }
  return id;
}

const claveDe = (id: string) => ["production-history", id] as const;
export const cabezaDeProduccion = (qc: QueryClient) => qc.getQueryData<Estado>(claveDe(sesion(qc)))?.history_id ?? null;

/**
 * La hoja abierta de un día (`POST …/days/{d}/open`, Baldosa y líneas): abrirla cuesta y congela el plan, así
 * que un gesto del tablero no la vuelve a pedir (contrato `tablero-29oct.md` §4). Solo se abre al abrirla o al
 * cambiar de día; cuando lo escrito la toca (`TOCAN_LA_HOJA`, Deshacer y Rehacer) se relee con la lectura sin
 * candado `GET …/board/days?since=d&until=d`, que devuelve la misma hoja (`releerHojas`, V2 de
 * `tablero-velocidad-31oct.md`).
 */
const esHojaDelDia = (k: QueryKey) => k[3] === "day" && (k[1] === "board" || k[2] === "board");

/** Lo que no toca la hoja abierta del día: el predicado de quien invalida `["production"]` sin escribir partes. */
export const noEsHojaDelDia = (q: { queryKey: QueryKey }) => !esHojaDelDia(q.queryKey);

/**
 * Las opciones de esa hoja: no caduca sola (ni al volver a la ventana) y al cerrarla se olvida (`gcTime: 0`), así
 * que abrirla o cambiar de día siempre la pide de nuevo; lo que la toca la relee. Sin reintentos: abrirla toma el
 * candado del tablero y un 5xx repetido dejaría «cargando» el triple (T10); el error trae su «Reintentar».
 */
export const HOJA_ABIERTA = { staleTime: Infinity, gcTime: 0, retry: false } as const;

/** Lo que cambia la hoja de un día: sus partes, su cierre, su cantidad y las barras quitadas o restauradas. */
const TOCAN_LA_HOJA = new Set<AccionDeProduccion["kind"]>(["cell", "day_close", "day_plan", "bar_remove", "bar_restore"]);

/**
 * Los gestos de una persona en UNA línea (V3): solo cambian esa línea y las que comparten a esa gente (la
 * capacidad física es una entre Baldosa, Calados y Fundidos). Los demás (archivar, prioridad, días extra,
 * bodega…) y Deshacer/Rehacer releen todo, como antes.
 */
const DE_UNA_LINEA = new Set<AccionDeProduccion["kind"]>([
  "program", "patch", "split", "return", "cut", "resize", "reorder", "replenish", "shift_selection", "team",
  "day_plan", "cell", "day_close", "bar_remove", "bar_restore",
]);
const LINEAS: readonly LineaDelHistorial[] = ["BA", "CA", "FU"];

/** La línea de una clave del tablero: `["production","board","BA",…]` o `["line-production","CA"|"FU","board",…]`. */
function lineaDeLaClave(k: QueryKey): LineaDelHistorial | null {
  if (k[0] === "production" && k[1] === "board" && k[2] === "BA") return "BA";
  if (k[0] === "line-production" && (k[1] === "CA" || k[1] === "FU") && k[2] === "board") return k[1];
  return null;
}

/** La gente de una línea según sus tableros ya leídos; `null` si no hay ninguno en caché. */
function genteDe(qc: QueryClient, linea: LineaDelHistorial): Set<string> | null {
  const raiz = linea === "BA" ? ["production", "board", "BA"] : ["line-production", linea, "board"];
  let leido = false;
  const gente = new Set<string>();
  for (const [, datos] of qc.getQueriesData<ProductionBoard | LineBoard>({ queryKey: raiz })) {
    const filas = datos && typeof datos === "object" && ("artisans" in datos ? datos.artisans : "operators" in datos ? datos.operators : null);
    if (!Array.isArray(filas)) continue;
    leido = true;
    for (const f of filas) gente.add(f.employee_id);
  }
  return leido ? gente : null;
}

/**
 * La línea tocada y las que comparten alguien con ella. Lo que no se sabe cuenta como dependiente (lo seguro): sin
 * la gente de la tocada, todas; una línea sin tablero leído, también (su hoja abierta se relee).
 */
function lineasQueDependen(qc: QueryClient, linea: LineaDelHistorial): Set<LineaDelHistorial> {
  const propia = genteDe(qc, linea);
  if (!propia) return new Set(LINEAS);
  return new Set(LINEAS.filter((otra) => {
    const suya = otra === linea ? null : genteDe(qc, otra);
    return otra === linea || suya === null || [...suya].some((e) => propia.has(e));
  }));
}

/**
 * Relee las hojas abiertas (de esas líneas, o todas). Las de días pasados, con `GET …/board/days` de su fecha: sin
 * candado ni escritura, en vez de repetir el POST `open`. La de HOY (`is_today`), con el POST como antes, porque
 * congela el plan de quien se programó desde hoy (corrección A1; sin nada que congelar el servidor no toma el
 * candado). Una hoja que ya se cerró no se vuelve a crear (`gcTime: 0` la olvidó); si la
 * lectura falla o no trae la fecha, se abre como siempre.
 */
function releerHojas(qc: QueryClient, lineas: Set<LineaDelHistorial> | null) {
  for (const q of qc.getQueryCache().findAll({ predicate: (x) => esHojaDelDia(x.queryKey) })) {
    const key = q.queryKey;
    const linea = lineaDeLaClave(key);
    const fecha = key[4];
    if (!linea || typeof fecha !== "string" || (lineas && !lineas.has(linea))) continue;
    const abrir = () => { void qc.invalidateQueries({ queryKey: key, exact: true }).catch(() => { /* La hoja muestra su error. */ }); };
    // A1: la de HOY (o una aún sin leer) se abre con el POST, como antes: congela el plan de una pareja nueva.
    if ((q.state.data as { is_today?: boolean } | undefined)?.is_today !== false) { abrir(); continue; }
    const rango = new URLSearchParams({ since: fecha, until: fecha });
    const ruta = linea === "BA" ? `/production/lines/BA/board/days?${rango}` : `/line-production/${linea}/board/days?${rango}`;
    void apiGet<{ date: string }[]>(ruta).then((hojas) => {
      const hoja = Array.isArray(hojas) ? hojas.find((h) => h.date === fecha) : undefined;
      if (!qc.getQueryCache().find({ queryKey: key, exact: true })) return;
      if (hoja) qc.setQueryData(key, hoja);
      else abrir();
    }, abrir);
  }
}

/**
 * No se espera a releer los tableros para confirmar una escritura ya guardada. `conHojas`: también la hoja
 * abierta del día (por la lectura sin candado); sin él, un gesto relee los tableros y deja la hoja como está.
 * `linea` (V3): el gesto fue de una persona en esa línea; se releen esa línea y las que comparten gente con ella,
 * y no la programación anterior (`["production","plans"]`, que el tablero no cambia y se relee sola cada 60 s).
 */
export function releerProduccion(qc: QueryClient, conHojas = false, linea: LineaDelHistorial | null = null) {
  const lineas = linea ? lineasQueDependen(qc, linea) : null;
  const predicate = (q: { queryKey: QueryKey }) => {
    const k = q.queryKey;
    if (esHojaDelDia(k)) return false;
    if (!lineas) return true;
    if (k[0] === "production" && k[1] === "plans") return false;
    const de = lineaDeLaClave(k);
    return !de || lineas.has(de);
  };
  for (const key of ["production", "line-production", "payroll-panel", "order-timeline"])
    void qc.invalidateQueries({ queryKey: [key], predicate }).catch(() => { /* La consulta muestra su propio error y Reintentar. */ });
  if (conHojas) releerHojas(qc, lineas);
}

/** Serializa intenciones de esta pestaña; cada una valida la cabeza confirmada por el servidor. */
async function enOrden(qc: QueryClient, hacer: (id: string, estado: Estado) => Promise<Resultado>, conHojas: boolean, linea: LineaDelHistorial | null = null) {
  const id = sesion(qc);
  pendiente(id, 1);
  const anterior = colas.get(id) ?? Promise.resolve();
  const siguiente = anterior.catch(() => undefined).then(async () => {
    await qc.cancelQueries({ queryKey: claveDe(id), exact: true });
    const cache = qc.getQueryState<Estado>(claveDe(id));
    const estado = cache?.data && !cache.isInvalidated ? cache.data : await apiGet<Estado>(`${RUTA}/${id}`);
    try {
      const r = await hacer(id, estado);
      qc.setQueryData(claveDe(id), r);
      releerProduccion(qc, conHojas, linea);
      return r;
    } catch (e) {
      // Una escritura ajena o un error nunca se disfraza como guardado ni se reintenta a ciegas.
      void qc.invalidateQueries({ queryKey: claveDe(id) });
      releerProduccion(qc, conHojas, linea);
      throw e;
    }
  });
  colas.set(id, siguiente);
  try { return await siguiente; }
  finally { pendiente(id, -1); if (colas.get(id) === siguiente) colas.delete(id); }
}

export function ejecutarProduccion(qc: QueryClient, line: LineaDelHistorial, actions: AccionDeProduccion[], command_id: string = crypto.randomUUID()) {
  return enOrden(qc, (session_id, estado) => {
    const previa = intenciones.get(command_id);
    // Si la respuesta se pierde, repetir conserva también la cabeza ORIGINAL: forma parte de la huella.
    const body = previa && previa.session_id === session_id && previa.line === line && JSON.stringify(previa.actions) === JSON.stringify(actions)
      ? previa : { session_id, command_id, line, expected_head: estado.history_id, actions } satisfies Esquema<"BoardHistoryCommand">;
    intenciones.set(command_id, body);
    if (intenciones.size > 128) intenciones.delete(intenciones.keys().next().value!);
    return apiPost<Resultado>(`${RUTA}/commands`, body).catch((error: unknown) => {
      // Un rechazo HTTP transaccional confirma que no se guardó; otra cabeza puede habilitar un nuevo intento.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) intenciones.delete(command_id);
      throw error;
    });
  }, actions.some((a) => TOCAN_LA_HOJA.has(a.kind)), actions.every((a) => DE_UNA_LINEA.has(a.kind) && (a.line ?? line) === line) ? line : null);
}

/** Adaptador de una acción existente: conserva el DTO original de su resultado. */
export async function accionDeProduccion<T>(qc: QueryClient, line: LineaDelHistorial, action: AccionDeProduccion, command_id: string = crypto.randomUUID()): Promise<T> {
  const r = await ejecutarProduccion(qc, line, [action], command_id);
  return r.results[0] as T;
}

/** `cabeza`: deshacer solo si el último paso sigue siendo ese (el «Deshacer» de un aviso). */
export function recorrerHistorial(qc: QueryClient, redo = false, command_id: string = crypto.randomUUID(), cabeza?: string) {
  return enOrden(qc, (id, estado) => {
    if (cabeza !== undefined && estado.history_id !== cabeza)
      return Promise.reject(new Error("después hubo otro cambio; usa el historial del tablero"));
    const key = `${id}:${redo}:${command_id}`;
    const body = compensaciones.get(key) ?? { command_id, expected_head: estado.history_id } satisfies Esquema<"BoardHistoryApply">;
    compensaciones.set(key, body);
    if (compensaciones.size > 128) compensaciones.delete(compensaciones.keys().next().value!);
    return apiPost<Resultado>(`${RUTA}/${id}/${redo ? "redo" : "undo"}`, body).catch((error: unknown) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) compensaciones.delete(key);
      throw error;
    });
    // Deshacer o rehacer puede tocar un parte: la hoja abierta se relee.
  }, true);
}

export function useHistorialDeProduccion() {
  const qc = useQueryClient();
  const usuario = useUsuario();
  const id = sesion(qc);
  const enCamino = useSyncExternalStore((fn) => { oyentes.add(fn); return () => { oyentes.delete(fn); }; }, () => pendientes.get(id) ?? 0);
  const estado = useQuery({ queryKey: claveDe(id), queryFn: () => apiGet<Estado>(`${RUTA}/${id}`), enabled: !!usuario.data });
  const intento = useRef<{ redo: boolean; id: string } | null>(null);
  const cambio = useMutation({ mutationFn: (redo: boolean) => {
    if (!intento.current || intento.current.redo !== redo) intento.current = { redo, id: crypto.randomUUID() };
    return recorrerHistorial(qc, redo, intento.current.id);
  }, onSuccess: () => { intento.current = null; } });
  return { ...estado.data, pendiente: cambio.isPending || enCamino > 0, cambiar: cambio.mutateAsync };
}
